import { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Check, X, Loader2 } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import { SECTION_COLOR, prijmeniJmeno } from '../lide';
import { Pole, Vstup, Vyber, Tlacitko, Hlaska, muted, linka } from '../karta/ui';

/**
 * Nahrávání osobních dokumentů — rychlé akce se začátkem a koncem → modal
 * (norma §1f). Dřív se oba formuláře rozbalovaly přímo ve stránce nad
 * seznamem a pětikrokový průvodce ho odsouval dolů.
 */

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

function useTypyDokumentu() {
    const [typy, setTypy] = useState([]);
    useEffect(() => {
        fetch('/api/ciselniky/typy-dokumentu').then(r => r.json()).then(d => setTypy(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);
    return typy;
}

/**
 * Bývalí zaměstnanci (neaktivní) — dokument jim jde nahrát taky; do systému se
 * nepřihlásí, takže jim přijde e-mailem s osobním odkazem (OdkazyKDokumentum).
 * Seznam se načte jednou, až je modal otevřený.
 */
function useByvali() {
    const [byvali, setByvali] = useState([]);
    useEffect(() => {
        fetch('/api/zamestnanci?archiv=1', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : []))
            .then(d => setByvali(Array.isArray(d) ? d.filter(z => z.stav === 'neaktivni') : [])).catch(() => {});
    }, []);
    return byvali;
}

const podleJmena = (a, b) => prijmeniJmeno(a).localeCompare(prijmeniJmeno(b), 'cs');
const lideProVyber = (zamestnanci, byvali = []) => [
    ...zamestnanci.filter(z => z.stav !== 'neaktivni').sort(podleJmena).map(z => ({ value: z.id, label: prijmeniJmeno(z) })),
    ...byvali.sort(podleJmena).map(z => ({ value: z.id, label: `${prijmeniJmeno(z)} · bývalý zaměstnanec` })),
];

/** Upozornění, že dokument půjde e-mailem s odkazem (vybraný je bývalý). */
function PoznamkaByvaly({ isDark }) {
    return (
        <p className="text-[12.5px]" style={{ gridColumn: '1 / -1', margin: 0, color: muted(isDark), lineHeight: 1.5 }}>
            Bývalý zaměstnanec se do systému nepřihlásí — dokument mu přijde e-mailem s osobním odkazem,
            kde si ho stáhne a případně podepíše.
        </p>
    );
}

/** Jeden dokument jednomu zaměstnanci. */
export function NahratModal({ zamestnanci, isDark, isMobile, onZavri, onHotovo, vychoziZamestnanec = null }) {
    const typy = useTypyDokumentu();
    const byvali = useByvali();
    const [f, setF] = useState({ zamestnanec_id: vychoziZamestnanec || '', typ_dokumentu_id: '', nazev: '', popis: '', expiruje_at: '', soubor: null });
    const [bezi, setBezi] = useState(false);
    const [chyba, setChyba] = useState(null);
    const souborRef = useRef(null);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));
    const lze = f.zamestnanec_id && f.nazev && f.soubor;

    const nahrat = async () => {
        setBezi(true); setChyba(null);
        const fd = new FormData();
        fd.append('nazev', f.nazev); fd.append('popis', f.popis);
        if (f.typ_dokumentu_id) fd.append('typ_dokumentu_id', f.typ_dokumentu_id);
        if (f.expiruje_at) fd.append('expiruje_at', f.expiruje_at);
        fd.append('soubor', f.soubor);
        try {
            const res = await fetch(`/api/zamestnanci/${f.zamestnanec_id}/dokumenty`, { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() }, body: fd });
            if (res.ok) { onHotovo(); onZavri(); return; }
            const e = await res.json().catch(() => ({}));
            setChyba({ typ: 'chyba', text: e.error || e.message || 'Chyba při nahrávání' });
        } catch {
            setChyba({ typ: 'chyba', text: 'Chyba při nahrávání' });
        }
        setBezi(false);
    };

    return (
        <ModalShell onClose={onZavri} isDark={isDark} barva={SECTION_COLOR} ikona={Upload} nadpis="Nahrát dokument" sirka={520}
            paticka={(
                <div className="flex gap-2 justify-end">
                    <Tlacitko isDark={isDark} vyska={40} onClick={onZavri}>Zrušit</Tlacitko>
                    <Tlacitko isDark={isDark} vyska={40} primarni disabled={!lze || bezi} onClick={nahrat}>{bezi ? 'Nahrávám…' : 'Nahrát'}</Tlacitko>
                </div>
            )}>
            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <Pole label="Zaměstnanec *" isDark={isDark}>
                    <Vyber isDark={isDark} value={f.zamestnanec_id} onChange={v => set('zamestnanec_id', v)} options={lideProVyber(zamestnanci, byvali)} />
                </Pole>
                <Pole label="Typ dokumentu" isDark={isDark}>
                    <Vyber isDark={isDark} value={f.typ_dokumentu_id} onChange={v => set('typ_dokumentu_id', v)} options={typy.map(t => ({ value: t.id, label: t.nazev }))} />
                </Pole>
                <Pole label="Název *" isDark={isDark}><Vstup isDark={isDark} value={f.nazev} onChange={e => set('nazev', e.target.value)} /></Pole>
                <Pole label="Expiruje dne" isDark={isDark}><Vstup isDark={isDark} type="date" value={f.expiruje_at} onChange={e => set('expiruje_at', e.target.value)} /></Pole>
                <Pole label="Popis" isDark={isDark} siroky><Vstup isDark={isDark} value={f.popis} placeholder="Volitelný popis" onChange={e => set('popis', e.target.value)} /></Pole>
                <div className="flex items-center" style={{ gridColumn: '1 / -1', gap: 10 }}>
                    <input ref={souborRef} type="file" className="hidden" onChange={e => set('soubor', e.target.files[0] || null)} />
                    <Tlacitko isDark={isDark} vyska={40} onClick={() => souborRef.current?.click()}><Upload size={14} /> Vybrat soubor</Tlacitko>
                    <span className="text-[12.5px] truncate" style={{ color: f.soubor ? 'var(--text-primary)' : muted(isDark) }}>{f.soubor ? f.soubor.name : 'max 10 MB'}</span>
                </div>
                {byvali.some(z => String(z.id) === String(f.zamestnanec_id)) && <PoznamkaByvaly isDark={isDark} />}
                <div style={{ gridColumn: '1 / -1' }}><Hlaska hlaska={chyba} /></div>
            </div>
        </ModalShell>
    );
}

/**
 * Hromadné nahrání — průvodce: typ → soubory → společné údaje → přiřazení lidem
 * (párování podle jména v souboru) → výsledek.
 */
export function HromadneModal({ zamestnanci, isDark, isMobile, onZavri, onHotovo }) {
    const typy = useTypyDokumentu();
    const [krok, setKrok] = useState(1);
    const [typ, setTyp] = useState('');
    const [soubory, setSoubory] = useState([]);
    const [meta, setMeta] = useState({ nazev: '', popis: '', expiruje_at: '' });
    const [parovani, setParovani] = useState(null);
    const [bezi, setBezi] = useState(false);
    const [vysledek, setVysledek] = useState(null);
    const souboryRef = useRef(null);
    const mut = muted(isDark);
    const byvali = useByvali();
    const lide = lideProVyber(zamestnanci, byvali);

    const vybrane = async (seznam) => {
        const pole = Array.from(seznam);
        setSoubory(pole);
        setBezi(true);
        try {
            const res = await fetch('/api/doberecne/parovani', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ nazvy_souboru: pole.map(s => s.name) }),
            });
            const data = await res.json();
            setParovani(data.map(d => ({ ...d, zamestnanec_id: d.zamestnanec_id || '' })));
        } catch {
            setParovani(pole.map(s => ({ filename: s.name, zamestnanec_id: '', confidence: 'none' })));
        }
        setBezi(false);
        setKrok(3);
    };

    const nahrat = async () => {
        setBezi(true);
        const fd = new FormData();
        fd.append('typ_dokumentu_id', typ);
        fd.append('nazev', meta.nazev);
        if (meta.popis) fd.append('popis', meta.popis);
        if (meta.expiruje_at) fd.append('expiruje_at', meta.expiruje_at);
        soubory.forEach((s, i) => { fd.append(`soubory[${i}]`, s); fd.append(`prirazeni[${i}]`, parovani[i].zamestnanec_id); });
        try {
            const res = await fetch('/api/dokumenty/hromadne', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
            setVysledek(await res.json());
        } catch {
            setVysledek({ message: 'Chyba při nahrávání', count: 0 });
        }
        setBezi(false);
        setKrok(5);
        onHotovo();
    };

    const chybiPrirazeni = parovani?.some(p => !p.zamestnanec_id);
    const paticka = (() => {
        if (krok === 5) return <Tlacitko isDark={isDark} vyska={40} primarni onClick={onZavri}>Hotovo</Tlacitko>;
        return (
            <div className="flex gap-2 justify-end">
                <Tlacitko isDark={isDark} vyska={40} onClick={krok > 1 && krok !== 3 ? () => setKrok(k => k - 1) : onZavri}>
                    {krok > 1 && krok !== 3 ? 'Zpět' : 'Zrušit'}
                </Tlacitko>
                {krok === 1 && <Tlacitko isDark={isDark} vyska={40} primarni disabled={!typ} onClick={() => setKrok(2)}>Pokračovat</Tlacitko>}
                {krok === 3 && <Tlacitko isDark={isDark} vyska={40} primarni disabled={!meta.nazev} onClick={() => setKrok(4)}>Pokračovat k přiřazení</Tlacitko>}
                {krok === 4 && (
                    <Tlacitko isDark={isDark} vyska={40} primarni disabled={bezi || chybiPrirazeni} onClick={nahrat}>
                        {bezi ? 'Nahrávám…' : `Nahrát (${parovani.filter(p => p.zamestnanec_id).length})`}
                    </Tlacitko>
                )}
            </div>
        );
    })();

    return (
        <ModalShell onClose={onZavri} isDark={isDark} barva={SECTION_COLOR} ikona={Upload}
            nadpis={krok === 5 ? 'Hromadné nahrání' : `Hromadné nahrání · krok ${krok}/4`} sirka={620} paticka={paticka}>
            <div style={{ padding: 16 }}>
                {krok === 1 && (
                    <Pole label="Typ dokumentu pro všechny soubory" isDark={isDark}>
                        <Vyber isDark={isDark} value={typ} onChange={setTyp} options={typy.map(t => ({ value: t.id, label: t.nazev }))} placeholder="— Vyberte typ —" />
                    </Pole>
                )}

                {krok === 2 && (
                    <button type="button" onClick={() => souboryRef.current?.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); if (e.dataTransfer.files.length) vybrane(e.dataTransfer.files); }}
                        className="w-full text-center"
                        style={{ border: `2px dashed ${isDark ? 'rgba(255,255,255,0.18)' : 'var(--border-default)'}`, borderRadius: 10, padding: '40px 16px', background: 'none', cursor: 'pointer' }}>
                        {bezi ? <Loader2 size={28} className="animate-spin" style={{ color: mut, margin: '0 auto' }} /> : <Upload size={28} style={{ color: mut, margin: '0 auto' }} />}
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', marginTop: 10 }}>{isMobile ? 'Vybrat soubory' : 'Přetáhněte soubory sem'}</p>
                        {!isMobile && <p className="text-xs" style={{ color: mut, marginTop: 4 }}>nebo klikněte pro výběr</p>}
                        <input ref={souboryRef} type="file" multiple className="hidden" onChange={e => { if (e.target.files.length) vybrane(e.target.files); }} />
                    </button>
                )}

                {krok === 3 && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                        <p className="text-[13px]" style={{ gridColumn: '1 / -1', color: 'var(--text-secondary)' }}>{soubory.length} souborů vybráno. Vyplňte společné údaje.</p>
                        <Pole label="Název dokumentů *" isDark={isDark}><Vstup isDark={isDark} value={meta.nazev} onChange={e => setMeta(m => ({ ...m, nazev: e.target.value }))} /></Pole>
                        <Pole label="Expiruje dne" isDark={isDark}><Vstup isDark={isDark} type="date" value={meta.expiruje_at} onChange={e => setMeta(m => ({ ...m, expiruje_at: e.target.value }))} /></Pole>
                        <Pole label="Popis" isDark={isDark} siroky><Vstup isDark={isDark} value={meta.popis} placeholder="Volitelný popis" onChange={e => setMeta(m => ({ ...m, popis: e.target.value }))} /></Pole>
                    </div>
                )}

                {krok === 4 && parovani && (
                    <div>
                        {parovani.map((p, i) => (
                            <div key={i} className={isMobile ? 'flex flex-col' : 'flex items-center'} style={{ gap: isMobile ? 6 : 12, padding: '10px 0', borderBottom: `1px solid ${linka(isDark)}` }}>
                                <span className="flex items-center min-w-0 flex-1" style={{ gap: 8 }}>
                                    <FileText size={14} style={{ color: mut, flexShrink: 0 }} />
                                    <span className="text-[13px] truncate" style={{ color: 'var(--text-primary)' }}>{p.filename}</span>
                                </span>
                                <span style={{ width: isMobile ? '100%' : 220 }}>
                                    <Vyber isDark={isDark} value={p.zamestnanec_id} options={lide}
                                        onChange={v => setParovani(prev => prev.map((x, j) => (j === i ? { ...x, zamestnanec_id: v, confidence: v ? 'manual' : 'none' } : x)))} />
                                </span>
                                <span className="text-[11.5px]" style={{ width: 80, textAlign: isMobile ? 'left' : 'center', color: p.confidence === 'none' ? '#f87171' : mut, fontWeight: p.confidence === 'none' ? 600 : 500 }}>
                                    {{ high: 'auto', ambiguous: 'více shod', none: 'nenalezen', manual: 'ručně' }[p.confidence] || ''}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {krok === 5 && vysledek && (
                    <div className="text-center" style={{ padding: '24px 8px' }}>
                        <div className="flex items-center justify-center mx-auto" style={{ width: 52, height: 52, borderRadius: '50%', background: vysledek.count > 0 ? 'rgba(5,150,105,0.14)' : 'rgba(239,68,68,0.14)' }}>
                            {vysledek.count > 0 ? <Check size={26} style={{ color: '#34d399' }} /> : <X size={26} style={{ color: '#f87171' }} />}
                        </div>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', marginTop: 12 }}>{vysledek.message}</p>
                    </div>
                )}
            </div>
        </ModalShell>
    );
}
